import classNames from "classnames";
import { useState } from "react";
import { ModalBody } from "reactstrap";
import { Trans, useTranslation } from "react-i18next";

import useCookies from "@hooks/useCookies";
import { Button } from "../Button";
import { InputSwitcher } from "../InputSwitcher";
import { localstorageKeys } from "@const/store";
import storageService from "../../services/StorageService";
import routes from "../../const/routes";

import styles from "./index.module.scss";

const OptionItem = ({
  isChecked,
  onChange,
  titleKey,
  descriptionKey,
  disabled,
}) => {
  const { t } = useTranslation("cookies-banner");

  const [isExpanded, setIsExpanded] = useState(false);

  const toggle = () => {
    setIsExpanded((prevState) => !prevState);
  };
  return (
    <div
      className={classNames(
        styles.optionContainer,
        "d-flex bg-inputs cursor-pointer",
      )}
      onClick={toggle}
    >
      <div className={styles.arrowContainer}>
        <div
          className={classNames(isExpanded && styles.arrowRotate, styles.arrow)}
        />
      </div>
      <div className="w-100">
        <div className="d-flex align-items-center w-100 justify-content-between">
          <h4 className="mb-0 text-body-1 text-medium text-primary">
            {t(titleKey)}
          </h4>
          <InputSwitcher
            isChecked={isChecked}
            onChange={onChange}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        {isExpanded && (
          <span className="text-body-2 text-regular mt-1 d-block text-grey-1">
            {t(descriptionKey)}
          </span>
        )}
      </div>
    </div>
  );
};

const CustomizeCookiesSelection = ({ isOpen, toggle }) => {
  const { t } = useTranslation("buttons");
  const { t: t1 } = useTranslation("customize-cookies-selection");

  const { setFunctionalCookies, setMarketingCookies, setNecessaryCookies } =
    useCookies();

  const [marketing, setMarketing] = useState(
    storageService.get(localstorageKeys.marketingCookies, false),
  );
  const [functional, setFunctional] = useState(
    storageService.get(localstorageKeys.functionalCookies, false),
  );

  const optionsConfig = [
    {
      id: "options-config-1",
      titleKey: "NECESSARY",
      descriptionKey: "NECESSARY_DESCRIPTION",
      disabled: true,
      isChecked: true,
    },
    {
      id: "options-config-2",
      titleKey: "MARKETING",
      descriptionKey: "MARKETING_DESCRIPTION",
      onChange: () => {
        setMarketing((prevState) => (!prevState ? new Date() : false));
      },
      isChecked: marketing,
    },
    {
      id: "options-config-3",
      titleKey: "FUNCTIONAL",
      descriptionKey: "FUNCTIONAL_DESCRIPTION",
      onChange: () => {
        setFunctional((prevState) => (!prevState ? new Date() : false));
      },
      isChecked: functional,
    },
  ];

  const buttonsConfig = [
    {
      id: "buttons-config-1",
      textKey: "REJECT_NON_ESSENTIAL",
      color: "secondary",
      onClick: () => {
        setMarketing(false);
        setFunctional(false);
        setNecessaryCookies();
        setFunctionalCookies(false);
        setMarketingCookies(false);
        toggle();
      },
    },
    {
      id: "buttons-config-2",
      textKey: "SAVE_MY_PREFERENCES",
      color: "secondary",
      onClick: () => {
        setFunctionalCookies(functional);
        setMarketingCookies(marketing);
        setNecessaryCookies();
        toggle();
      },
    },
    {
      id: "buttons-config-3",
      textKey: "ACCEPT_ALL",
      color: "primary",
      onClick: () => {
        setMarketing(true);
        setFunctional(true);
        setFunctionalCookies(true);
        setMarketingCookies(true);
        setNecessaryCookies();
        toggle();
      },
    },
  ];

  const goToCookiePolicy = () => {
    window.open(routes.COOKIE_POLICY(), "_blank", "noopener,noreferrer");
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className={styles.backdrop} />

      <div className={styles.modal}>
        <h3 className="h3 text-primary text-center">{t1("TITLE")}</h3>

        <ModalBody className={classNames("p-0 overflow-hidden", styles.body)}>
          <p className="text-body-1 text-primary text-secondary">
            <Trans
              t={t1}
              components={{
                span: (
                  <span
                    className="text-orange cursor-pointer"
                    onClick={goToCookiePolicy}
                  />
                ),
              }}
              i18nKey="DESCRIPTION"
            />
          </p>
          <div>
            {optionsConfig.map((config) => (
              <OptionItem {...config} key={config.id} />
            ))}
          </div>
        </ModalBody>
        <div
          className={classNames("d-flex gap-3", styles.footerButtonsContainer)}
        >
          {buttonsConfig.map((config) => (
            <Button
              size="md"
              {...config}
              key={config.id}
              className={styles.btn}
            >
              {t(config.textKey)}
            </Button>
          ))}
        </div>
      </div>
    </>
  );
};

export default CustomizeCookiesSelection;
